import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API_BASE } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { StatCard } from "@/components/ui/StatCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Activity, FileDown, FileSpreadsheet, FileText, Send, Briefcase, GitBranch, FileCheck2,
  Sparkles, Calculator, Mail, Inbox, Phone, ListChecks, CheckCircle2, MessageSquare,
  ShieldCheck, Settings2, ExternalLink, CalendarDays,
} from "lucide-react";

const CAT_ICON = {
  bids: Briefcase, revisions: GitBranch, proposals: FileCheck2, leads: Sparkles,
  estimating: Calculator, followups: Mail, inbound: Inbox, outreach: Phone,
  tasks: ListChecks, reviews: CheckCircle2, notes: MessageSquare, approvals: ShieldCheck,
  admin: Settings2,
};

const iso = (d) => d.toISOString().slice(0, 10);
function presetRange(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = (today.getDay() + 6) % 7; // Mon=0
  const monday = new Date(today); monday.setDate(today.getDate() - dow);
  if (key === "today") return { from: iso(today), to: iso(today) };
  if (key === "week") return { from: iso(monday), to: iso(today) };
  if (key === "lastweek") {
    const lastMon = new Date(monday); lastMon.setDate(monday.getDate() - 7);
    const lastSun = new Date(monday); lastSun.setDate(monday.getDate() - 1);
    return { from: iso(lastMon), to: iso(lastSun) };
  }
  if (key === "month") {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(first), to: iso(today) };
  }
  return { from: iso(monday), to: iso(today) };
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "lastweek", label: "Last week" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom" },
];

export default function MyWork() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = presetRange("week");
  const initialRange = {
    from: params.get("from") || initial.from,
    to: params.get("to") || initial.to,
  };
  const matchPreset = (r) => {
    for (const k of ["today", "week", "lastweek", "month"]) {
      const pr = presetRange(k);
      if (pr.from === r.from && pr.to === r.to) return k;
    }
    return "custom";
  };
  const [preset, setPreset] = useState(() => matchPreset(initialRange));
  const [range, setRange] = useState(initialRange);
  const [targetUser, setTargetUser] = useState(params.get("user") || "");
  const [people, setPeople] = useState({ viewable: [], share_targets: [], manager_id: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingOptin, setSavingOptin] = useState(false);

  // share dialog
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTo, setShareTo] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [emailPdf, setEmailPdf] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get("/worklog/people").then((r) => {
      setPeople(r.data || {});
      if (!shareTo && r.data?.manager_id) setShareTo(r.data.manager_id);
      else if (!shareTo && (r.data?.share_targets || []).length) setShareTo(r.data.share_targets[0].user_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    const p = { from: range.from, to: range.to };
    if (targetUser) p.user_id = targetUser;
    api.get("/worklog", { params: p })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load your work report"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [range.from, range.to, targetUser]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== "custom") setRange(presetRange(key));
  };

  const download = async (ext, mime) => {
    try {
      const p = new URLSearchParams({ from: range.from, to: range.to });
      if (targetUser) p.set("user_id", targetUser);
      const res = await api.get(`/worklog/export.${ext}?${p.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GridForge_WorkReport_${range.from}_to_${range.to}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const toggleOptin = async (enabled) => {
    setSavingOptin(true);
    try {
      await api.post("/worklog/weekly-optin", { enabled });
      setData((d) => (d ? { ...d, weekly_optin: enabled } : d));
      toast.success(enabled ? "You'll get a recap every Friday afternoon" : "Weekly recap turned off");
    } catch { toast.error("Could not update"); }
    finally { setSavingOptin(false); }
  };

  const submitShare = async () => {
    if (!shareTo) { toast.error("Choose a teammate to share with"); return; }
    setSharing(true);
    try {
      const body = { to_user_id: shareTo, from: range.from, to: range.to, note: shareNote.trim(), email_pdf: emailPdf };
      if (targetUser) body.user_id = targetUser;
      const { data: res } = await api.post("/worklog/share", body);
      toast.success(res?.emailed ? "Shared and emailed the report" : "Shared with your teammate");
      setShareOpen(false); setShareNote("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not share"); }
    finally { setSharing(false); }
  };

  // Keep URL params in sync so shared/deep-linked reports open on the right view.
  useEffect(() => {
    const next = {};
    if (targetUser) next.user = targetUser;
    next.from = range.from; next.to = range.to;
    setParams(next, { replace: true });
    // eslint-disable-next-line
  }, [targetUser, range.from, range.to]);

  const hl = data?.headline || {};
  const isSelf = data?.is_self ?? true;
  const viewName = data?.user?.name || "Teammate";
  const managerView = (people.viewable || []).length > 1;
  const canShare = (people.share_targets || []).length > 0;
  const shareTargets = people.share_targets || [];

  const grouped = useMemo(() => {
    const items = data?.items || [];
    const byDay = {};
    for (const it of items) {
      const day = (it.at || "").slice(0, 10) || "—";
      (byDay[day] = byDay[day] || []).push(it);
    }
    return Object.entries(byDay).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [data]);

  return (
    <div className="px-6 sm:px-8 py-8 max-w-[1800px] w-full mx-auto" data-testid="my-work-page">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-5 h-5 text-[#F4C400]" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-black">
          {isSelf ? "My Work" : `${viewName}'s Work`}
        </h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Everything you got done — bids, revisions, follow-ups, leads, calls and reviews — in one place, ready to share.
      </p>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="my-work-controls">
        {managerView && (
          <Select value={targetUser || (user?.user_id || "")} onValueChange={(v) => setTargetUser(v === (user?.user_id || "") ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="my-work-person"><SelectValue placeholder="Whose work?" /></SelectTrigger>
            <SelectContent>
              {(people.viewable || []).map((p) => (
                <SelectItem key={p.user_id} value={p.user_id} data-testid={`my-work-person-${p.user_id}`}>
                  {p.user_id === user?.user_id ? "Me" : p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="my-work-presets">
          {PRESETS.map((p) => (
            <button key={p.key} data-testid={`my-work-preset-${p.key}`} onClick={() => applyPreset(p.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition ${preset === p.key ? "bg-[#003E87] text-white" : "text-slate-500 hover:text-black"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-1.5" data-testid="my-work-custom-range">
            <CalendarDays className="w-4 h-4 text-slate-400" />
            <Input type="date" value={range.from} data-testid="my-work-from" onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-9 w-40" />
            <span className="text-slate-400 text-sm">→</span>
            <Input type="date" value={range.to} data-testid="my-work-to" onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-9 w-40" />
          </div>
        )}
      </div>

      {/* Exports + share */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Button variant="outline" size="sm" data-testid="my-work-export-pdf" onClick={() => download("pdf", "application/pdf")}>
          <FileText className="w-4 h-4 mr-1.5 text-rose-600" /> PDF
        </Button>
        <Button variant="outline" size="sm" data-testid="my-work-export-csv" onClick={() => download("csv", "text/csv")}>
          <FileDown className="w-4 h-4 mr-1.5 text-slate-600" /> CSV
        </Button>
        <Button variant="outline" size="sm" data-testid="my-work-export-xlsx" onClick={() => download("xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")}>
          <FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-600" /> Excel
        </Button>
        {canShare && (
          <Button size="sm" data-testid="my-work-share-btn" onClick={() => setShareOpen(true)}
            className="bg-[#003E87] hover:bg-[#00306a] text-white">
            <Send className="w-4 h-4 mr-1.5" /> Share with manager
          </Button>
        )}
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="my-work-kpi-submitted" icon={Briefcase} accent="#003E87" label="Bids submitted" value={hl.submitted ?? 0} />
        <StatCard testid="my-work-kpi-revisions" icon={GitBranch} accent="#6366F1" label="Revisions" value={hl.revisions ?? 0} />
        <StatCard testid="my-work-kpi-followups" icon={Mail} accent="#F4C400" label="Follow-ups sent" value={hl.followups ?? 0} />
        <StatCard testid="my-work-kpi-total" icon={Activity} accent="#10B981" label="Total actions" value={hl.total ?? 0} />
      </div>

      {/* Category breakdown */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="my-work-categories">
        {(data?.categories || []).filter((c) => c.count > 0).map((c) => {
          const Icon = CAT_ICON[c.key] || Activity;
          return (
            <span key={c.key} data-testid={`my-work-cat-${c.key}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600">
              <Icon className="w-3.5 h-3.5 text-[#003E87]" /> {c.label}
              <span className="tabular-nums font-bold text-black">{c.count}</span>
            </span>
          );
        })}
        {!loading && (data?.categories || []).every((c) => c.count === 0) && (
          <span className="text-sm text-slate-400">No activity recorded in this range.</span>
        )}
      </div>

      {/* Weekly recap opt-in (only for your own report) */}
      {isSelf && (
        <Card className="p-4 mb-8 flex items-center gap-3" data-testid="my-work-weekly-optin">
          <div className="w-9 h-9 rounded-lg bg-[#F4C400]/15 text-[#8a6d00] grid place-items-center shrink-0">
            <CalendarDays className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800">Weekly recap email</div>
            <div className="text-xs text-slate-500">Get a summary of everything you did, emailed to you every Friday afternoon.</div>
          </div>
          <Switch data-testid="my-work-weekly-switch" checked={!!data?.weekly_optin} disabled={savingOptin} onCheckedChange={toggleOptin} />
        </Card>
      )}

      {/* Activity log grouped by day */}
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="w-4 h-4 text-slate-400" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Activity log</h2>
        <span className="text-xs text-slate-400 font-mono">{data?.items?.length || 0}</span>
      </div>
      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-400">Loading…</Card>
      ) : (data?.items?.length || 0) === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-400" data-testid="my-work-empty">
          <Inbox className="w-6 h-6 mx-auto mb-2 text-slate-300" />
          Nothing logged in this range yet. Pick a wider window, or get to work — it all shows up here.
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([day, items]) => (
            <div key={day} data-testid={`my-work-day-${day}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                {day === "—" ? "Undated" : fmtDate(day)}
              </div>
              <div className="space-y-2">
                {items.map((it, i) => {
                  const Icon = CAT_ICON[it.category] || Activity;
                  const inner = (
                    <>
                      <div className="w-8 h-8 rounded-lg bg-slate-100 text-[#003E87] grid place-items-center shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-black truncate">{it.title}</div>
                        {it.detail && <div className="text-xs text-slate-500 truncate">{it.detail}</div>}
                      </div>
                      <div className="text-[11px] text-slate-400 shrink-0 tabular-nums">{(it.at || "").slice(11, 16)}</div>
                      {it.link && <ExternalLink className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                    </>
                  );
                  const cls = "flex items-center gap-3 p-3 transition";
                  return it.link ? (
                    <a key={i} href={it.link} data-testid={`my-work-item-${day}-${i}`}
                      className="block" onClick={(e) => { if (it.link.startsWith(window.location.origin)) { /* internal deep-link */ } }}>
                      <Card className={`${cls} cursor-pointer hover:border-[#003E87]/40`}>{inner}</Card>
                    </a>
                  ) : (
                    <Card key={i} data-testid={`my-work-item-${day}-${i}`} className={cls}>{inner}</Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="my-work-share-dialog">
          <DialogHeader>
            <DialogTitle>Share this work report</DialogTitle>
            <DialogDescription>
              Tag a teammate and optionally email them the PDF for {range.from} → {range.to}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Share with</Label>
              <Select value={shareTo} onValueChange={setShareTo}>
                <SelectTrigger className="h-9" data-testid="my-work-share-target"><SelectValue placeholder="Choose a teammate" /></SelectTrigger>
                <SelectContent>
                  {shareTargets.map((p) => (
                    <SelectItem key={p.user_id} value={p.user_id} data-testid={`my-work-share-target-${p.user_id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <textarea data-testid="my-work-share-note" value={shareNote} onChange={(e) => setShareNote(e.target.value)} rows={3}
                placeholder="Anything you want them to know…" className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 resize-none" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="my-work-share-email" checked={emailPdf} onChange={(e) => setEmailPdf(e.target.checked)} className="rounded border-slate-300" />
              Also email them the report
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button data-testid="my-work-share-send" onClick={submitShare} disabled={sharing} className="bg-[#003E87] hover:bg-[#00306a] text-white">
              {sharing ? "Sharing…" : "Share"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
